import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';

/**
 * Wraps a route to require authentication. Behavior:
 *
 * - **Loading**: muestra spinner centrado (la primera fetch del /me).
 * - **No autenticado**: redirige a `/login?returnUrl=<currentPath>`. Login lee el query y
 *   re-redirige tras login exitoso.
 * - **Autenticado**: renderiza `children` normalmente.
 *
 * El componente NO hace nada con el error — si /me falla con un status NO-401, el caller
 * decide cómo manejarlo (ej: pantalla de error a nivel app). Para el flujo normal, 401 ya
 * lo trata el AuthProvider como "no autenticado" y caemos al redirect.
 *
 * Story SW-311.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Preservar el path completo (pathname + search) para post-login redirect.
    const returnUrl = `${location.pathname}${location.search}`;
    const loginUrl = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    return <Navigate to={loginUrl} replace />;
  }

  return <>{children}</>;
}
